import { sendMessageWithMpcWallet, apiVersion, uploadFileWithMpcWallet } from "./dwcommonutils";
import { concatLogsAndVars, createEmptyLogsAndVars, recordVariable, ReportingError, } from "../diagnostics/clientErrors";
import { jwtChunkedMessageAsGetReturnsBinary, jwtMessageAsGetReturnsBinary, jwtMessageAsGetReturnsJson, jwtUploadFileAndMessageReturnsJson, myReadFileInChunksAsReadableStream, } from "./jwtcomm";
import { splitPath } from "../paths";
import { decryptBlob } from "../dataEncryption";
const MAX_CHUNK_SIZE = process.env.MAX_CHUNK_SIZE ? parseInt(process.env.MAX_CHUNK_SIZE) : 67_108_836;
const MAX_CHUNK_SIZE_GROSS = MAX_CHUNK_SIZE + 12 + 16;
export const getMyFilesList = async (portalClientId, clientSessionToken, ed25519UserShare, path, recurse) => {
    let logsAndVars = createEmptyLogsAndVars("dwstorageutils:getMyFilesList");
    recordVariable(logsAndVars, "portalClientId (param)", portalClientId);
    recordVariable(logsAndVars, "clientSessionToken (param)", clientSessionToken);
    recordVariable(logsAndVars, "path (param)", path);
    recordVariable(logsAndVars, "recurse (param)", recurse);
    const sendObject = {
        method: "get",
        endpoint: `/api/${apiVersion}/files/list`,
        clientId: portalClientId,
        path: path,
        recurse: recurse,
    };
    recordVariable(logsAndVars, "sendObject", sendObject);
    try {
        return await sendMessageWithMpcWallet(clientSessionToken, ed25519UserShare, sendObject);
    }
    catch (error) {
        if (error instanceof ReportingError) {
            logsAndVars = concatLogsAndVars(logsAndVars, error.logsAndVars);
        }
        throw new ReportingError("Getting files list has failed. Error message was: " + error.message, logsAndVars);
    }
};
export const getMyFilesListJwt = async (authToken, path, recurse) => {
    const sendObject = {
        path,
        recurse,
    };
    return await jwtMessageAsGetReturnsJson("get", `/api/${apiVersion}/files/list`, authToken, sendObject);
};
export const myWriteFile = async (portalClientId, clientSessionToken, ed25519UserShare, path, file) => {
    let logsAndVars = createEmptyLogsAndVars("dwstorageutils:myWriteFile");
    recordVariable(logsAndVars, "portalClientId (param)", portalClientId);
    recordVariable(logsAndVars, "clientSessionToken (param)", clientSessionToken);
    recordVariable(logsAndVars, "path (param)", path);
    recordVariable(logsAndVars, "file (param)", file);
    const sendObject = {
        method: "post",
        endpoint: `/api/${apiVersion}/files/write`,
        clientId: portalClientId,
        path: path,
    };
    recordVariable(logsAndVars, "sendObject", sendObject);
    try {
        return await uploadFileWithMpcWallet(clientSessionToken, ed25519UserShare, sendObject, file);
    }
    catch (error) {
        if (error instanceof ReportingError) {
            logsAndVars = concatLogsAndVars(logsAndVars, error.logsAndVars);
        }
        throw new ReportingError("Writing file has failed. Error message was: " + error.message, logsAndVars);
    }
};
export const myWriteFileJwt = async (authToken, path, file, onProgress) => {
    let logsAndVars = createEmptyLogsAndVars("dwstorageutils:myWriteFileJwt");
    recordVariable(logsAndVars, "authToken (param)", authToken);
    recordVariable(logsAndVars, "path (param)", path);
    recordVariable(logsAndVars, "file (param)", file);
    const sendObject = {
        path: path,
    };
    recordVariable(logsAndVars, "sendObject", sendObject);
    try {
        //console.log(sendObject);
        return await jwtUploadFileAndMessageReturnsJson("post", `/api/${apiVersion}/files/write`, authToken, sendObject, file, onProgress);
    }
    catch (error) {
        if (error instanceof ReportingError) {
            logsAndVars = concatLogsAndVars(logsAndVars, error.logsAndVars);
        }
        throw new ReportingError("Writing file has failed. Error message was: " + error.message, logsAndVars);
    }
};
export const myReadFile = async (portalClientId, clientSessionToken, ed25519UserShare, path) => {
    let logsAndVars = createEmptyLogsAndVars("dwstorageutils:myReadFile");
    recordVariable(logsAndVars, "portalClientId (param)", portalClientId);
    recordVariable(logsAndVars, "clientSessionToken (param)", clientSessionToken);
    recordVariable(logsAndVars, "path (param)", path);
    if (path === "") {
        return new Blob([], { type: "application/octet-stream" });
    }
    const sendObject = {
        method: "get",
        endpoint: `/api/${apiVersion}/files/read`,
        clientId: portalClientId,
        path: path,
    };
    recordVariable(logsAndVars, "sendObject", sendObject);
    try {
        return await sendMessageWithMpcWallet(clientSessionToken, ed25519UserShare, sendObject, "blob");
    }
    catch (error) {
        if (error instanceof ReportingError) {
            logsAndVars = concatLogsAndVars(logsAndVars, error.logsAndVars);
        }
        throw new ReportingError("Reading file has failed. Error message was: " + error.message, logsAndVars);
    }
};
export const myReadFileJwt = async (authToken, path, onProgress, signal) => {
    let logsAndVars = createEmptyLogsAndVars("dwstorageutils:myReadFile");
    recordVariable(logsAndVars, "authToken (param)", authToken);
    recordVariable(logsAndVars, "path (param)", path);
    if (path === "") {
        return new ReadableStream();
    }
    const sendObject = {
        path: path,
    };
    recordVariable(logsAndVars, "sendObject", sendObject);
    try {
        return await jwtMessageAsGetReturnsBinary("get", `/api/${apiVersion}/files/read`, authToken, sendObject, onProgress, signal);
    }
    catch (error) {
        if (signal?.aborted ||
            error?.name === "CanceledError" ||
            error?.name === "AbortError" ||
            error?.code === "ERR_CANCELED") {
            throw error;
        }
        if (error instanceof ReportingError) {
            logsAndVars = concatLogsAndVars(logsAndVars, error.logsAndVars);
        }
        throw new ReportingError("Reading file has failed. Error message was: " + error.message, logsAndVars);
    }
};
export const determineIsChunked = async (authToken, path, signal) => {
    let logsAndVars = createEmptyLogsAndVars("dwstorageutils:determineIsChunked");
    recordVariable(logsAndVars, "authToken (param)", authToken);
    recordVariable(logsAndVars, "path (param)", path);
    if (path === "") {
        return false;
    }
    const sendObject = {
        path: path,
    };
    recordVariable(logsAndVars, "sendObject", sendObject);
    try {
        const fileMetadata = await jwtMessageAsGetReturnsJson("get", `/api/${apiVersion}/files/metadata`, authToken, sendObject, signal);
        return Boolean(fileMetadata.metadata.UPLOADED_IN_CHUNKS) ?? false;
    }
    catch (error) {
        if (signal?.aborted ||
            error?.name === "CanceledError" ||
            error?.name === "AbortError" ||
            error?.code === "ERR_CANCELED") {
            throw error;
        }
        if (error instanceof ReportingError) {
            logsAndVars = concatLogsAndVars(logsAndVars, error.logsAndVars);
        }
        //throw new ReportingError("getting metadata file has failed. Error message was: " + error.message, logsAndVars);
        return false;
    }
};
/*export const myReadFileInChunks = async (
  provider: EIP1193Provider,
  authToken: string,
  path: string,
  onProgress?: (progressEvent: AxiosProgressEvent) => void,
): Promise<File> => {
    let logsAndVars = createEmptyLogsAndVars("dwstorageutils:myReadFile");
  recordVariable(logsAndVars, "authToken (param)", authToken);
  recordVariable(logsAndVars, "path (param)", path);
  if (path === "") {
    return new File([], "file.bin", { type: "application/octet-stream" });
  }
  const pathParts = splitPath(path);
  const fileName = pathParts?.name ?? "file.bin";
  const sendObject = {
    path: path,
  };
  const CHUNK_SIZE = MAX_CHUNK_SIZE + 16 + 12;
  try {
    let readBytes = 1;
    let start = 0;
    let end = start + CHUNK_SIZE - 1;
    let finalBlob = new Blob([], { type: "application/octet-stream" });
    while (readBytes > 0) {
      const responseBlob = await jwtChunkedMessageAsGetReturnsBinary(
        "get",
        `/api/${apiVersion}/files/read-part`,
        authToken,
        sendObject,
        start,
        end,
        onProgress,
      );
      readBytes = responseBlob.size;
      console.log(readBytes);
      if (readBytes > 0) {
        const decryptedBlob = await decryptBlob(provider, responseBlob);
        finalBlob = mergeBlobs([finalBlob, decryptedBlob]);
        start += CHUNK_SIZE;
        end += CHUNK_SIZE;
      }
    }
    return new File([finalBlob], fileName);
  } catch (error: any) {
    if (error instanceof ReportingError) {
      logsAndVars = concatLogsAndVars(logsAndVars, error.logsAndVars);
    }
    throw new ReportingError("Reading file has failed. Error message was: " + error.message, logsAndVars);
  }
};*/
export const readFileInChunksAsReadableStream = async (provider, authToken, path, size, progressData, onProgress, signal) => {
    return myReadFileInChunksAsReadableStream(provider, `/api/${apiVersion}/files/read-part`, authToken, path, size, progressData, calculateEstimated, onProgress, signal);
};
export const myReadFileInChunksToWritable = async (provider, authToken, path, progressData, writable, onProgress) => {
    let logsAndVars = createEmptyLogsAndVars("dwstorageutils:myReadFile");
    recordVariable(logsAndVars, "authToken (param)", authToken);
    recordVariable(logsAndVars, "path (param)", path);
    if (path === "") {
        return;
    }
    const pathParts = splitPath(path);
    const fileName = pathParts?.name ?? "file.bin";
    const sendObject = {
        path: path,
    };
    const writer = writable.getWriter();
    const CHUNK_SIZE = MAX_CHUNK_SIZE + 16 + 12;
    try {
        let readBytes = CHUNK_SIZE;
        let readAllBytes = 0;
        let start = 0;
        let end = start + CHUNK_SIZE - 1;
        const startTime = Date.now();
        //let finalBlob = new Blob([], { type: "application/octet-stream" });
        while (readBytes === CHUNK_SIZE) {
            console.log("before jwtChunk");
            let responseBlob = await jwtChunkedMessageAsGetReturnsBinary("get", `/api/${apiVersion}/files/read-part`, authToken, sendObject, start, end, (progress) => {
                if (onProgress) {
                    onProgress({
                        fileId: progressData.fileId,
                        status: progressData.status,
                        loaded: readAllBytes + progress.loaded,
                        total: progressData.size,
                        estimated: calculateEstimated(startTime, progressData.loadedFilesBytes + readAllBytes + progress.loaded, progressData.totalFilesBytes),
                        loadedFiles: progressData.loadedFiles,
                        totalFiles: progressData.totalFiles,
                        loadedFilesBytes: progressData.loadedFilesBytes,
                        totalFilesBytes: progressData.totalFilesBytes,
                    });
                }
            });
            readBytes = responseBlob.size;
            readAllBytes += responseBlob.size;
            if (readBytes > 0) {
                let decryptedBlob = await decryptBlob(provider, responseBlob);
                //await decryptedBlob.stream().pipeTo(writable);
                await writer.write(await decryptedBlob.arrayBuffer());
                decryptedBlob = null;
                start += CHUNK_SIZE;
                end += CHUNK_SIZE;
            }
            responseBlob = null;
            console.log(readBytes, CHUNK_SIZE);
        }
        writer.close();
        //return new File([finalBlob], fileName);
    }
    catch (error) {
        if (error instanceof ReportingError) {
            logsAndVars = concatLogsAndVars(logsAndVars, error.logsAndVars);
        }
        throw new ReportingError("Reading file has failed. Error message was: " + error.message, logsAndVars);
    }
};
function calculateEstimated(startTimestamp, loadedBytes, totalBytes) {
    const currentTimestamp = Date.now();
    const elapsedMillis = currentTimestamp - startTimestamp;
    const rate = elapsedMillis / loadedBytes;
    return (rate * (totalBytes - loadedBytes)) / 1000.0;
}
function mergeBlobs(blobs) {
    return new Blob(blobs, { type: "application/octet-stream" });
}
/*export const myReadFile3 = async (path: string, jwt: string) => {
  let logsAndVars = createEmptyLogsAndVars("dwstorageutils:myReadFile3");
  recordVariable(logsAndVars, "jwt (param)", jwt);
  if (path === "") {
    return new Blob([], { type: "application/octet-stream" });
  }
  const sendObject = {
    method: "get",
    endpoint: `/api/0.3/files/read`,
    jwt: jwt,
    path: path,
  };
  recordVariable(logsAndVars, "sendObject", sendObject);
  try {
    return await plainWithJWT(jwt, "get", ) sendMessageWithJWT(jwt, sendObject, "blob");
  } catch (error: any) {
    if (error instanceof ReportingError) {
      logsAndVars = concatLogsAndVars(logsAndVars, error.logsAndVars);
    }
    throw new ReportingError("Reading file has failed. Error message was: " + error.message, logsAndVars);
  }
};*/
export const getMyFileStat = async (portalClientId, clientSessionToken, ed25519UserShare, path) => {
    let logsAndVars = createEmptyLogsAndVars("dwstorageutils:getMyFileStat");
    recordVariable(logsAndVars, "portalClientId (param)", portalClientId);
    recordVariable(logsAndVars, "clientSessionToken (param)", clientSessionToken);
    recordVariable(logsAndVars, "path (param)", path);
    const sendObject = {
        method: "get",
        endpoint: `/api/${apiVersion}/files/stat`,
        clientId: portalClientId,
        path: path,
    };
    recordVariable(logsAndVars, "sendObject", sendObject);
    try {
        return await sendMessageWithMpcWallet(clientSessionToken, ed25519UserShare, sendObject);
    }
    catch (error) {
        if (error instanceof ReportingError) {
            logsAndVars = concatLogsAndVars(logsAndVars, error.logsAndVars);
        }
        throw new ReportingError("Getting file statistics has failed. Error message was: " + error.message, logsAndVars);
    }
};
export const myFileExists = async (portalClientId, clientSessionToken, ed25519UserShare, path) => {
    let logsAndVars = createEmptyLogsAndVars("dwstorageutils:myFileExists");
    recordVariable(logsAndVars, "portalClientId (param)", portalClientId);
    recordVariable(logsAndVars, "clientSessionToken (param)", clientSessionToken);
    recordVariable(logsAndVars, "path (param)", path);
    const sendObject = {
        method: "get",
        endpoint: `/api/${apiVersion}/files/exists`,
        clientId: portalClientId,
        path: path,
    };
    recordVariable(logsAndVars, "sendObject", sendObject);
    try {
        const result = await sendMessageWithMpcWallet(clientSessionToken, ed25519UserShare, sendObject);
        return result.exists;
    }
    catch (error) {
        if (error instanceof ReportingError) {
            logsAndVars = concatLogsAndVars(logsAndVars, error.logsAndVars);
        }
        throw new ReportingError("Checking file existence has failed. Error message was: " + error.message, logsAndVars);
    }
};
export const myFileExistsJwt = async (authToken, path) => {
    const sendObject = {
        path,
    };
    const response = await jwtMessageAsGetReturnsJson("get", `/api/${apiVersion}/files/exists`, authToken, sendObject);
    return response.exists;
};
export const myRemoveFile = async (portalClientId, clientSessionToken, ed25519UserShare, path) => {
    let logsAndVars = createEmptyLogsAndVars("dwstorageutils:myRemoveFile");
    recordVariable(logsAndVars, "portalClientId (param)", portalClientId);
    recordVariable(logsAndVars, "clientSessionToken (param)", clientSessionToken);
    recordVariable(logsAndVars, "path (param)", path);
    if (path === "") {
        return;
    }
    const sendObject = {
        method: "delete",
        endpoint: `/api/${apiVersion}/files/remove`,
        clientId: portalClientId,
        path: path,
    };
    recordVariable(logsAndVars, "sendObject", sendObject);
    try {
        return await sendMessageWithMpcWallet(clientSessionToken, ed25519UserShare, sendObject);
    }
    catch (error) {
        if (error instanceof ReportingError) {
            logsAndVars = concatLogsAndVars(logsAndVars, error.logsAndVars);
        }
        throw new ReportingError("Removing file has failed. Error message was: " + error.message, logsAndVars);
    }
};
export const myRemoveFileJwt = async (authToken, path) => {
    const sendObject = {
        path: path,
    };
    return await jwtMessageAsGetReturnsJson("delete", `/api/${apiVersion}/files/remove`, authToken, sendObject);
};
export const myRemoveAllJwt = async (authToken) => {
    const sendObject = {};
    return await jwtMessageAsGetReturnsJson("delete", `/api/${apiVersion}/files/remove-all`, authToken, sendObject);
};
export const myCopyFile = async (portalClientId, clientSessionToken, ed25519UserShare, path, newPath) => {
    let logsAndVars = createEmptyLogsAndVars("dwstorageutils:myCopyFile");
    recordVariable(logsAndVars, "portalClientId (param)", portalClientId);
    recordVariable(logsAndVars, "clientSessionToken (param)", clientSessionToken);
    recordVariable(logsAndVars, "path (param)", path);
    recordVariable(logsAndVars, "newPath (param)", newPath);
    const sendObject = {
        method: "put",
        endpoint: `/api/${apiVersion}/files/copy`,
        clientId: portalClientId,
        path: path,
        newPath: newPath,
    };
    recordVariable(logsAndVars, "sendObject", sendObject);
    try {
        return await sendMessageWithMpcWallet(clientSessionToken, ed25519UserShare, sendObject);
    }
    catch (error) {
        if (error instanceof ReportingError) {
            logsAndVars = concatLogsAndVars(logsAndVars, error.logsAndVars);
        }
        throw new ReportingError("Copying file has failed. Error message was: " + error.message, logsAndVars);
    }
};
export const myMoveFile = async (portalClientId, clientSessionToken, ed25519UserShare, path, newPath) => {
    let logsAndVars = createEmptyLogsAndVars("dwstorageutils:myMoveFile");
    recordVariable(logsAndVars, "portalClientId (param)", portalClientId);
    recordVariable(logsAndVars, "clientSessionToken (param)", clientSessionToken);
    recordVariable(logsAndVars, "path (param)", path);
    recordVariable(logsAndVars, "newPath (param)", newPath);
    const sendObject = {
        method: "post",
        endpoint: `/api/${apiVersion}/files/move`,
        clientId: portalClientId,
        path: path,
        newPath: newPath,
    };
    recordVariable(logsAndVars, "sendObject", sendObject);
    try {
        return await sendMessageWithMpcWallet(clientSessionToken, ed25519UserShare, sendObject);
    }
    catch (error) {
        if (error instanceof ReportingError) {
            logsAndVars = concatLogsAndVars(logsAndVars, error.logsAndVars);
        }
        throw new ReportingError("Moving file has failed. Error message was: " + error.message, logsAndVars);
    }
};
export const getMyFileMetadata = async (portalClientId, clientSessionToken, ed25519UserShare, path) => {
    let logsAndVars = createEmptyLogsAndVars("dwstorageutils:getMyFileMetadata");
    recordVariable(logsAndVars, "portalClientId (param)", portalClientId);
    recordVariable(logsAndVars, "clientSessionToken (param)", clientSessionToken);
    recordVariable(logsAndVars, "path (param)", path);
    const sendObject = {
        method: "get",
        endpoint: `/api/${apiVersion}/files/metadata`,
        clientId: portalClientId,
        path: path,
    };
    recordVariable(logsAndVars, "sendObject", sendObject);
    try {
        return await sendMessageWithMpcWallet(clientSessionToken, ed25519UserShare, sendObject);
    }
    catch (error) {
        if (error instanceof ReportingError) {
            logsAndVars = concatLogsAndVars(logsAndVars, error.logsAndVars);
        }
        throw new ReportingError("Getting file metadata has failed. Error message was: " + error.message, logsAndVars);
    }
};
export const getMyFileMetadataJwt = async (authToken, path) => {
    const sendObject = {
        path,
    };
    return await jwtMessageAsGetReturnsJson("get", `/api/${apiVersion}/files/metadata`, authToken, sendObject);
};
export const setMyFileMetadata = async (portalClientId, clientSessionToken, ed25519UserShare, path, metadata) => {
    let logsAndVars = createEmptyLogsAndVars("dwstorageutils:setMyFileMetadata");
    recordVariable(logsAndVars, "portalClientId (param)", portalClientId);
    recordVariable(logsAndVars, "clientSessionToken (param)", clientSessionToken);
    recordVariable(logsAndVars, "path (param)", path);
    recordVariable(logsAndVars, "metadata (param)", metadata);
    const sendObject = {
        method: "put",
        endpoint: `/api/${apiVersion}/files/metadata`,
        clientId: portalClientId,
        path: path,
    };
    recordVariable(logsAndVars, "sendObject", sendObject);
    const metadataParameter = {
        metadata: metadata,
    };
    recordVariable(logsAndVars, "metadataParameter", metadataParameter);
    try {
        return await sendMessageWithMpcWallet(clientSessionToken, ed25519UserShare, sendObject, undefined, metadataParameter);
    }
    catch (error) {
        if (error instanceof ReportingError) {
            logsAndVars = concatLogsAndVars(logsAndVars, error.logsAndVars);
        }
        throw new ReportingError("Setting file metadata has failed. Error message was: " + error.message, logsAndVars);
    }
};
